import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import LmsMenu from './lmspages/LmsMenu';
import MyCourses from './lmspages/MyCourses';
import CourseCatalog from './lmspages/CourseCatalog';
import Settings from './lmspages/Settings';
import Workshop from './Workshop';
import { BASE_URL } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/ProgramPages.css';
import '../styles/events.css';
import './lmspages/LmsMenu.css';
import './lmspages/LmsLayout.css';

function Lms() {
  const navigate = useNavigate();
  const userName = localStorage.getItem('userName') || 'Learner';
  const [activeSection, setActiveSection] = useState('dashboard');
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({
    activeCourses: 0,
    certificates: 0,
    learningTime: 0
  });
  const [loading, setLoading] = useState(true);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    console.log('Active section:', section);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || 1;

      // Fetch user enrollments
      const enrollmentResponse = await fetch(`${BASE_URL}/enrollments/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (enrollmentResponse.ok) {
        const result = await enrollmentResponse.json();
        if (result.success) {
          const enrollmentData = result.data || [];
          setEnrollments(enrollmentData);

          // Calculate stats
          const activeCourses = enrollmentData.filter(e => e.status === 'active' || e.status === 'in_progress').length;
          const certificates = enrollmentData.filter(e => e.status === 'completed').length;
          const totalMinutes = enrollmentData.reduce((sum, e) => sum + (e.time_spent_minutes || 0), 0);
          const learningHours = Math.round(totalMinutes / 60);

          setStats({
            activeCourses,
            certificates,
            learningTime: learningHours
          });
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueCourse = (courseId) => {
    navigate(`/player?courseId=${courseId}`);
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active' || e.status === 'in_progress');
  const completedEnrollments = enrollments.filter(e => e.status === 'completed');
  const completionRate = enrollments.length > 0
    ? Math.round((completedEnrollments.length / enrollments.length) * 100)
    : 0;

  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="ep-loading">
          <div className="ep-spinner" />
          <p>Loading dashboard...</p>
        </div>
      );
    }

    return (
      <div className="lms-ep-dashboard">
        <header className="ep-hero lms-ep-hero">
          <div className="ep-hero-glow" aria-hidden="true" />
          <div className="ep-hero-inner lms-ep-hero-inner">
            <div>
              <span className="ep-live-badge">
                <span className="ep-pulse-dot" />
                LMS Dashboard
              </span>
              <h1 className="ep-hero-title">
                Welcome back, <span className="ep-red">{userName}</span>
              </h1>
              <p className="ep-hero-sub">
                Continue your learning path, track course progress, and jump back into lessons in one click.
              </p>
              <div className="ep-hero-actions">
                <button className="ep-btn-primary" onClick={() => setActiveSection('courses')}>
                  Open My Courses →
                </button>
                <button className="ep-btn-ghost" onClick={() => setActiveSection('catalog')}>
                  Browse Catalog
                </button>
                <button className="ep-btn-ghost" onClick={() => setActiveSection('settings')}>
                  Profile Settings
                </button>
              </div>
            </div>

            <div className="ep-featured-card">
              <div className="ep-featured-glow" aria-hidden="true" />
              <div className="ep-featured-inner">
                <div className="ep-featured-top">
                  <span className="ep-featured-label">Learning Snapshot</span>
                  <span className="ep-featured-date-label">This week</span>
                </div>
                <h2 className="ep-featured-title">{stats.activeCourses} courses in progress</h2>
                <p className="ep-featured-desc">
                  You have completed {completedEnrollments.length} out of {enrollments.length} enrolled course(s).
                </p>
                <div className="lms-ep-progress-track">
                  <div className="lms-ep-progress-fill" style={{ width: `${completionRate}%` }} />
                </div>
                <div className="lms-ep-progress-meta">
                  <span>{completionRate}% completion rate</span>
                  <span>{stats.learningTime}h learned</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="ep-stats-section lms-ep-stats-section">
          <div className="ep-stats-inner">
            <div className="ep-stats-grid">
              <div className="ep-stat-card">
                <div className="ep-stat-n">{stats.activeCourses}</div>
                <div className="ep-stat-l">Active Courses</div>
              </div>
              <div className="ep-stat-card">
                <div className="ep-stat-n">{stats.certificates}</div>
                <div className="ep-stat-l">Completed Courses</div>
              </div>
              <div className="ep-stat-card">
                <div className="ep-stat-n">{stats.learningTime}h</div>
                <div className="ep-stat-l">Learning Time</div>
              </div>
              <div className="ep-stat-card">
                <div className="ep-stat-n">{completionRate}%</div>
                <div className="ep-stat-l">Completion Rate</div>
              </div>
            </div>
          </div>
        </section>

        <section className="ep-events-section lms-ep-courses-section">
          <div className="ep-events-inner">
            <div className="ep-events-header">
              <div>
                <h2 className="ep-section-title">Continue Learning</h2>
                <p className="ep-muted ep-count-label">
                  {activeEnrollments.length} active course{activeEnrollments.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {activeEnrollments.length > 0 ? (
              <div className="ep-event-list">
                {activeEnrollments.slice(0, 6).map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="ep-event-row lms-ep-course-row"
                    onClick={() => handleContinueCourse(enrollment.course_id)}
                  >
                    <div className="ep-row-date">
                      <div className="ep-row-day">Progress</div>
                      <div className="ep-row-d">{Math.round(enrollment.progress_percentage || 0)}</div>
                      <div className="ep-row-mo">%</div>
                    </div>
                    <div className="ep-row-details">
                      <div className="ep-row-title-wrap">
                        <h3 className="ep-row-title">{enrollment.course_title}</h3>
                        <span className="ep-hot-badge">In Progress</span>
                      </div>
                      <p className="ep-row-desc">
                        Course code: {enrollment.course_code || 'N/A'}
                      </p>
                      <div className="ep-row-chips">
                        <span className="ep-chip ep-chip-muted">⏱️ {Math.round((enrollment.time_spent_minutes || 0) / 60)}h spent</span>
                        <span className="ep-chip ep-chip-free">Status: {enrollment.status}</span>
                      </div>
                    </div>
                    <div className="ep-row-action" onClick={(e) => e.stopPropagation()}>
                      <div className="ep-row-attendees">Last update: {enrollment.updated_at ? new Date(enrollment.updated_at).toLocaleDateString() : 'N/A'}</div>
                      <button className="ep-btn-primary ep-btn-sm" onClick={() => handleContinueCourse(enrollment.course_id)}>
                        Continue →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ep-empty">
                <div className="ep-empty-icon">📚</div>
                <h3>No active courses yet</h3>
                <p className="ep-muted">Start learning by enrolling from the catalog.</p>
                <button className="ep-btn-primary" onClick={() => setActiveSection('catalog')}>
                  Browse Courses →
                </button>
              </div>
            )}

            {completedEnrollments.length > 0 && (
              <>
                <h3 className="ep-past-title">Completed Courses</h3>
                <div className="ep-event-list ep-past-list">
                  {completedEnrollments.slice(0, 3).map((enrollment) => (
                    <div key={enrollment.id} className="ep-event-row ep-event-row-past lms-ep-course-row">
                      <div className="ep-row-date">
                        <div className="ep-row-day">Done</div>
                        <div className="ep-row-d">100</div>
                        <div className="ep-row-mo">%</div>
                      </div>
                      <div className="ep-row-details">
                        <div className="ep-row-title-wrap">
                          <h3 className="ep-row-title">{enrollment.course_title}</h3>
                          <span className="ep-past-badge">Completed</span>
                        </div>
                        <p className="ep-row-desc">Course code: {enrollment.course_code || 'N/A'}</p>
                        <div className="ep-row-chips">
                          <span className="ep-chip ep-chip-muted">
                            Completed: {enrollment.completed_at ? new Date(enrollment.completed_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="ep-row-action">
                        <div className="ep-row-attendees">Great work</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className="lms-container lms-ep-theme">
      <SEO 
        title="Learning Management System | Lead with Tech"
        description="Access our comprehensive Learning Management System with courses, workshops, and training materials."
        keywords="lms, learning management system, online courses, training, education"
      />

      <LmsMenu activeSection={activeSection} onSectionChange={handleSectionChange} />

      <div className="lms-main lms-ep-main">
        <div className="lms-content lms-ep-content">
          {activeSection === 'courses' ? (
            <MyCourses />
          ) : activeSection === 'my-workshops' ? (
            <Workshop />
          ) : activeSection === 'catalog' ? (
            <CourseCatalog />
          ) : activeSection === 'settings' ? (
            <Settings />
          ) : activeSection === 'dashboard' ? (
            renderDashboard()
          ) : (
            <div className="ep-empty lms-ep-placeholder">
              <div className="ep-empty-icon">
                {activeSection === 'catalog' && '🔍'}
                {activeSection === 'progress' && '📈'}
                {activeSection === 'certificates' && '🎓'}
                {activeSection === 'assignments' && '📝'}
                {activeSection === 'calendar' && '📅'}
                {activeSection === 'resources' && '📁'}
                {activeSection === 'settings' && '⚙️'}
              </div>
              <h2>{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Section</h2>
              <p className="ep-muted">This section is coming soon. Stay tuned for updates!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Lms;
